import { Axios } from "services/axios";
import { normalizeEntityOverrides } from "utils/entitySelectorUtils";

export const subscriptionApi = {
  createSubscription: async (subscriptionData) => {
    try {
      if (!subscriptionData) {
        throw new Error("Subscription data is required");
      }

      // Validate required fields
      const { subscriptionType, moduleType, duration, country } =
        subscriptionData;

      if (!subscriptionType) {
        throw new Error("Subscription type is required");
      }
      if (!moduleType) {
        throw new Error("Module type is required");
      }
      if (!duration) {
        throw new Error("Duration is required");
      }
      if (!country) {
        throw new Error("Country is required");
      }

      // console.log("Subscription data:", subscriptionData);
      const subscriptionFormattedData = {
        ...subscriptionData,
        moduleType: moduleType.toLowerCase(),
        subscriptionType: subscriptionType.toLowerCase(),
        pricing: normalizeEntityOverrides(subscriptionData.pricing),
      };

      // console.log("SubscriptionFormatted data:", subscriptionFormattedData);

      const response = await Axios.post(
        "/payment-framework/configure",
        subscriptionFormattedData
      );

      if (!response || !response.data) {
        throw new Error("Invalid response structure from server");
      }

      const responseData = response.data.data || response.data;

      return {
        success: true,
        data: responseData,
        message:
          response.data.message || "Payment framework created successfully",
      };
    } catch (error) {
      console.error("Error creating payment framework:", error);
      let errorMessage = "Failed to create payment framework";

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          error.response.data?.error ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = "Network error: Unable to reach server";
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  getAllSubscriptions: async (options = {}) => {
    try {
      const {
        page = 1,
        limit = 50,
        subscriptionType = "",
        moduleType = "",
        country = "",
        duration = "",
        sortBy = "createdAt",
        sortOrder = "DESC",
      } = options;

      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        sortBy,
        sortOrder,
      });

      if (subscriptionType) {
        params.append("subscriptionType", subscriptionType);
      }
      if (moduleType) {
        params.append("moduleType", moduleType);
      }
      if (country) {
        params.append("country", country);
      }
      if (duration) {
        params.append("duration", duration);
      }

      const response = await Axios.fetch(`/payment-framework?${params}`);

      if (!response || !response.data) {
        throw new Error("Invalid response structure from server");
      }

      const responseData = response.data.data || response.data || {};

      return responseData;
    } catch (error) {
      console.error("Error fetching payment frameworks:", error);

      let errorMessage = "Failed to fetch payment frameworks";

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = "Network error: Unable to reach server";
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  getSubscriptionById: async (id) => {
    try {
      if (!id) {
        throw new Error("Payment framework ID is required");
      }

      const response = await Axios.fetch(`/payment-framework/${id}`);

      if (!response || !response.data) {
        throw new Error("Invalid response structure from server");
      }

      const subscription = response.data.data || response.data;

      if (!subscription) {
        throw new Error("Payment framework not found");
      }

      return subscription;
    } catch (error) {
      console.error("Error fetching payment framework:", error);

      let errorMessage = "Failed to fetch payment framework";

      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = "Payment framework not found";
        } else {
          errorMessage =
            error.response.data?.message ||
            `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = "Network error: Unable to reach server";
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  updateSubscription: async (id, subscriptionData) => {
    try {
      if (!id) {
        throw new Error("Payment framework ID is required");
      }

      if (!subscriptionData) {
        throw new Error("Payment framework data is required");
      }

      // Same normalisation as createSubscription (backend expects lowercase module/type)
      const subscriptionFormattedData = {
        ...subscriptionData,
        moduleType: subscriptionData.moduleType?.toLowerCase(),
        subscriptionType: subscriptionData.subscriptionType?.toLowerCase(),
        pricing: normalizeEntityOverrides(subscriptionData.pricing),
      };

      const response = await Axios.put(
        `/payment-framework/${id}`,
        subscriptionFormattedData
      );

      if (!response || !response.data) {
        throw new Error("Invalid response structure from server");
      }

      const responseData = response.data.data || response.data;

      return {
        success: true,
        data: responseData,
        message:
          response.data.message || "Payment framework updated successfully",
      };
    } catch (error) {
      console.error("Error updating payment framework:", error);

      let errorMessage = "Failed to update payment framework";

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = "Network error: Unable to reach server";
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  deleteSubscription: async (id) => {
    try {
      if (!id) {
        throw new Error("Payment framework ID is required");
      }

      const response = await Axios.delete(`/payment-framework/${id}`);

      if (!response || !response.data) {
        throw new Error("Invalid response structure from server");
      }

      return {
        success: true,
        message:
          response.data.message || "Payment framework deleted successfully",
      };
    } catch (error) {
      console.error("Error deleting payment framework:", error);

      let errorMessage = "Failed to delete payment framework";

      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = "Payment framework not found";
        } else {
          errorMessage =
            error.response.data?.message ||
            `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = "Network error: Unable to reach server";
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  getAvailableModules: async () => {
    try {
      const response = await Axios.fetch("/payment-framework/modules/available");

      if (!response || !response.data) {
        throw new Error("Invalid response structure from server");
      }

      return response.data.data || response.data;
    } catch (error) {
      console.error("Error fetching available modules:", error);
      throw new Error("Failed to fetch available modules: " + error.message);
    }
  },

  getAvailableSubscriptionTypes: async () => {
    try {
      const response = await Axios.fetch(
        "/payment-framework/subscription-types/available"
      );

      if (!response || !response.data) {
        throw new Error("Invalid response structure from server");
      }

      return response.data.data || response.data;
    } catch (error) {
      console.error("Error fetching subscription types:", error);
      throw new Error("Failed to fetch subscription types: " + error.message);
    }
  },

  getAvailablePricingStrategies: async () => {
    try {
      const response = await Axios.fetch(
        "/payment-framework/pricing-strategies/available"
      );

      if (!response || !response.data) {
        throw new Error("Invalid response structure from server");
      }

      return response.data.data || response.data;
    } catch (error) {
      console.error("Error fetching pricing strategies:", error);
      throw new Error("Failed to fetch pricing strategies: " + error.message);
    }
  },

  getEntitiesForCustomPricing: async (moduleType) => {
    try {
      if (!moduleType) {
        throw new Error("Module type is required");
      }

      const response = await Axios.fetch(
        `/payment-framework/entities/custom-pricing?moduleType=${moduleType}`
      );

      if (!response || !response.data) {
        throw new Error("Invalid response structure from server");
      }

      return response.data.data || response.data;
    } catch (error) {
      console.error("Error fetching entities for custom pricing:", error);
      throw new Error("Failed to fetch entities: " + error.message);
    }
  },

  transformFormDataToPayload: (formData) => {
    // console.log("🔧 Transforming form data to payload:", formData);

    // Directly use the formData as it already has correct structure
    const payload = {
      subscriptionType: formData.subscriptionType,
      moduleType: formData.moduleType.toLowerCase(),
      duration: formData.duration,
      country: formData.country,
      pricing: {
        strategy: formData.pricing?.strategy || "global",
        globalDiscount: formData.pricing?.globalDiscount || 0,
        entityOverrides: formData.pricing?.entityOverrides || [],
      },
      config: formData.config || {
        coolingOffPeriod: { enabled: true, days: 14, refundPercentage: 100 },
        renewal: { autoRenew: true, gracePeriodDays: 7, reminderDaysBefore: 3 },
        cancellation: {
          allowed: false,
          allowedWithinDays: 30,
          refundPercentage: 0,
          adminApprovalRequired: false,
        },
        billing: { proRataRefund: false, billingCycles: 0, trialPeriodDays: 0 },
      },
    };

    // console.log("📦 Transformed payload:", payload);
    return payload;
  },

  validateSubscriptionData: (subscriptionData) => {
    console.log("Validating subscription data:", subscriptionData);

    const errors = [];

    if (!subscriptionData.subscriptionType) {
      errors.push("Subscription type is required");
    }

    if (!subscriptionData.moduleType) {
      errors.push("Module type is required");
    }

    if (!subscriptionData.duration) {
      errors.push("Duration is required");
    }

    if (!subscriptionData.country) {
      errors.push("Country is required");
    }

    if (subscriptionData.pricing?.strategy === "custom") {
      if (
        !subscriptionData.pricing.entityOverrides ||
        subscriptionData.pricing.entityOverrides.length === 0
      ) {
        errors.push(
          "At least one entity override is required for custom pricing"
        );
      } else {
        // Validate each entity override
        subscriptionData.pricing.entityOverrides.forEach((override, index) => {
          if (!override.entityId) {
            errors.push(
              `Entity ID is required for entity override at position ${
                index + 1
              }`
            );
          }
          if (!override.entityName) {
            errors.push(
              `Entity Name is required for entity override at position ${
                index + 1
              }`
            );
          }
          if (!override.entityType) {
            errors.push(
              `Entity Type is required for entity override at position ${
                index + 1
              }`
            );
          }
          if (
            override.discountPercentage === undefined ||
            override.discountPercentage === null
          ) {
            errors.push(
              `Discount percentage is required for ${
                override.entityName || "entity"
              }`
            );
          }
          if (
            override.discountPercentage < 0 ||
            override.discountPercentage > 100
          ) {
            errors.push(
              `Discount percentage must be between 0 and 100 for ${
                override.entityName || "entity"
              }`
            );
          }
        });
      }
    }

    const result = {
      isValid: errors.length === 0,
      errors,
    };

    console.log("📋 Validation result:", result);
    return result;
  },

  getAuditLogs: async (entityType, entityId, options = {}) => {
    try {
      const { page = 1, limit = 50 } = options;

      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });

      if (entityType) {
        params.append("entityType", entityType);
      }
      if (entityId) {
        params.append("entityId", entityId);
      }

      // UPDATED ROUTE: /payment-framework/audit-logs
      const response = await Axios.fetch(
        `/payment-framework/audit-logs?${params}`
      );

      if (!response || !response.data) {
        throw new Error("Invalid response structure from server");
      }

      return response.data.data || response.data;
    } catch (error) {
      console.error("Error fetching audit logs:", error);
      throw new Error("Failed to fetch audit logs: " + error.message);
    }
  },

  getAllFrameworks: async (options = {}) => {
    try {
      const {
        page = 1,
        limit = 50,
        isActive = null,
        includeModuleConfigs = false,
        includeCountryPolicies = false,
      } = options;

      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });

      if (isActive !== null) {
        params.append("isActive", isActive);
      }
      if (includeModuleConfigs) {
        params.append("includeModuleConfigs", includeModuleConfigs);
      }
      if (includeCountryPolicies) {
        params.append("includeCountryPolicies", includeCountryPolicies);
      }

      // ✅ UPDATED ROUTE: /payment-framework/frameworks
      const response = await Axios.fetch(
        `/payment-framework/frameworks?${params}`
      );

      if (!response || !response.data) {
        throw new Error("Invalid response structure from server");
      }

      return response.data.data || response.data;
    } catch (error) {
      console.error("Error fetching frameworks:", error);
      throw new Error("Failed to fetch frameworks: " + error.message);
    }
  },

  createFramework: async (frameworkData) => {
    try {
      if (!frameworkData) {
        throw new Error("Framework data is required");
      }

      // UPDATED ROUTE: /payment-framework/frameworks
      const response = await Axios.post(
        "/payment-framework/frameworks",
        frameworkData
      );

      if (!response || !response.data) {
        throw new Error("Invalid response structure from server");
      }

      return {
        success: true,
        data: response.data.data || response.data,
        message: response.data.message || "Framework created successfully",
      };
    } catch (error) {
      console.error("Error creating framework:", error);
      throw new Error("Failed to create framework: " + error.message);
    }
  },
};
